import type { PropsWithChildren } from "react";
import { Component } from "react";
import { captureException } from "@/utils/capture-exception";
import { isChunkLoadError } from "@/utils/new-version";

import { ErrorUpdateMessage } from "./error-update-message";

// A one-off error, such as a translator or extension changing the DOM under
// React, usually goes away on a fresh mount. A second error this soon after a
// remount stays on the error page.
const remountWindowMs = 10_000;

export class ErrorBoundary extends Component<
  PropsWithChildren,
  { hasError: boolean; isUpdate: boolean }
> {
  lastRemountAt = 0;

  constructor(props: PropsWithChildren) {
    super(props);
    this.state = { hasError: false, isUpdate: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error(error);
    return {
      hasError: true,
      isUpdate: isChunkLoadError(error),
    };
  }

  // Errors caught here never reach Sentry's global handlers. A stale chunk only
  // gets here when the tab could not reload onto the new version, so only real
  // crashes are reported, including the ones a remount recovers from.
  componentDidCatch(error: Error) {
    if (this.state.isUpdate) {
      return;
    }

    captureException(error);

    // Runs in the commit phase, so React remounts before the error page paints.
    const now = Date.now();
    if (now - this.lastRemountAt > remountWindowMs) {
      this.lastRemountAt = now;
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorUpdateMessage isUpdate={this.state.isUpdate} />;
    }

    return this.props.children;
  }
}
